<script lang="ts" setup>
interface Template {
  id: string;
  title: string;
}

const props = defineProps({
  templates: {
    type: Array as PropType<Template[]>,
    required: true,
  },
  originTemplateId: {
    type: String,
    default: null,
  },
});

// The list only holds released templates, so a project whose own template has
// since been set inactive would otherwise just silently lack its "origin" row.
const originMissing = computed(
  () =>
    !!props.originTemplateId &&
    !props.templates.some((template) => template.id === props.originTemplateId),
);

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'next', templateId: string): void;
}>();

const searchQuery = ref<string>('');

const templatesList = computed(() => {
  const queryString = searchQuery.value.trim().toLowerCase();
  if (!queryString) {
    return props.templates;
  }

  return props.templates.filter((template) =>
    template.title.toLowerCase().includes(queryString),
  );
});
</script>

<template>
  <div>
    <p class="subtitle">
      {{ $t('projectUpdateFromTemplateModal.selectTemplate') }}
    </p>
    <UiInput v-model="searchQuery" :placeholder="$t('random.search')">
      <template #icon>
        <UiIcon name="Search" />
      </template>
    </UiInput>

    <ul class="template-list">
      <div class="list-header">
        {{ $t('template.templateList') }}
        <UiIcon name="ArrowDown" width="15" />
      </div>

      <li
        v-for="(temp, i) in templatesList"
        :key="i"
        @click="emit('next', temp.id)"
      >
        <div>{{ temp.title }}</div>
        <div v-if="originTemplateId === temp.id">{{ 'origin' }}</div>
      </li>
    </ul>

    <p v-if="originMissing" class="origin-missing">
      {{ $t('template.originNotReleased') }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.subtitle {
  color: var(--color-gray-500);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-14);
  line-height: var(--line-height-20);
  padding-top: var(--space-12);
}

.origin-missing {
  color: var(--color-gray-500);
  font-size: var(--font-size-12);
  line-height: var(--line-height-18);
  padding-top: var(--space-8);
}

.list-header {
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-12);
  color: var(--color-gray-500);
  line-height: var(--line-height-18);
  padding: var(--space-16) var(--space-16);
  display: flex;
  gap: var(--space-8);
}

.template-list {
  border: 1px solid var(--color-gray-200);
  margin-top: var(--space-16);
  border-radius: var(--border-radius-4);
  padding-left: 0;
  max-height: px(500);
  overflow: auto;

  li {
    padding: var(--space-24) var(--space-16);
    display: flex;
    justify-content: space-between;
    gap: var(--space-16);
    align-items: center;
    color: var(--color-gray-900);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-14);
    line-height: var(--line-height-20);
    cursor: pointer;

    &:nth-child(even) {
      background: var(--color-gray-50);
    }
  }
}
</style>
